import {
    IYandexMetrikaInitParams,
    IYandexMetrikaCounterParams,
    IYandexMetrikaEvents,
    IYandexMetrikaApi,
    IYandexMetrika,
    YandexMetrikaMethods,
    IYandexMetrikaConstructor,
    GlobalCounter,
} from './types';

const ASYNC_LOAD_CALLBACK_NAME = 'yandex_metrika_callbacks2';
const SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js';
const DEFAULT_TIMEOUT = 2 * 1000; // 2 secs

const mapYandexMetrikaInitialParamsToCounterParams = (
    initialParams: IYandexMetrikaInitParams
): IYandexMetrikaCounterParams => ({
    id: initialParams.id,
    clickmap: initialParams.clickmap,
    trackLinks: initialParams.trackLinks,
    accurateTrackBounce: initialParams.accurateTrackBounce,
    webvisor: initialParams.webvisor,
    experiments: initialParams.experiments,
    params: initialParams.params,
    yaGDPRLang: initialParams.yaGdprLang,
});

export class YandexMetrikaApi implements IYandexMetrikaApi {
    private metrika: IYandexMetrika | null = null;

    private asyncEventsBuffer: IYandexMetrikaEvents[] = [];

    private params: IYandexMetrikaCounterParams;

    private scriptSrc: string;

    constructor({
        initialParams,
        scriptSrc = SCRIPT_SRC,
    }: {
        initialParams: IYandexMetrikaInitParams;
        scriptSrc?: string;
    }) {
        this.scriptSrc = scriptSrc;
        this.params = mapYandexMetrikaInitialParamsToCounterParams(initialParams);

        this.init();
    }

    private async init() {
        const Metrika = await this.load();

        if (!Metrika) {
            return;
        }

        this.metrika = new Metrika(this.params);

        this.initGlobalCounter(this.metrika);

        this.asyncEventsBuffer.forEach((event) => {
            this.handleMetrikaEvent(event);
        });

        this.asyncEventsBuffer = [];
    }

    private getMetrikaSdkApi = (): IYandexMetrikaConstructor | undefined => window.Ya?.Metrika2;

    private async doLoad() {
        const loadedMetrikaApi = this.getMetrikaSdkApi();

        if (loadedMetrikaApi) {
            return loadedMetrikaApi;
        }

        return new Promise<IYandexMetrikaConstructor>((resolve, reject) => {
            const handleError = () => {
                metrikaScriptElement?.remove();
                clearTimeout(timer);
                reject();
            };

            const timer = setTimeout(handleError, DEFAULT_TIMEOUT);

            let metrikaScriptElement = document.querySelector<HTMLScriptElement>(
                `script[src*="${this.scriptSrc}"]`
            );
            if (!metrikaScriptElement) {
                metrikaScriptElement = document.createElement('script');

                metrikaScriptElement.type = 'text/javascript';
                metrikaScriptElement.async = true;
                metrikaScriptElement.src = this.scriptSrc;
                metrikaScriptElement.onerror = handleError;

                document.body.append(metrikaScriptElement);
            }

            let asyncLoadCallback = window[ASYNC_LOAD_CALLBACK_NAME];

            if (!Array.isArray(asyncLoadCallback)) {
                asyncLoadCallback = [];

                window[ASYNC_LOAD_CALLBACK_NAME] = asyncLoadCallback;
            }

            asyncLoadCallback.push(() => {
                const metrikaApi = this.getMetrikaSdkApi();

                if (metrikaApi) {
                    clearTimeout(timer);
                    resolve(metrikaApi);
                } else {
                    handleError();
                }
            });
        });
    }

    private async load() {
        try {
            return await this.doLoad();
        } catch {
            return undefined;
        }
    }

    private initGlobalCounter(metrika: IYandexMetrika) {
        const counterKey: GlobalCounter = `yaCounter${this.params.id}`;
        window[counterKey] = metrika;
    }

    private handleMetrikaEvent(event: IYandexMetrikaEvents) {
        if (!this.metrika) {
            return;
        }

        switch (event.type) {
            case YandexMetrikaMethods.params:
                this.metrika.params(...event.payload);
                break;

            case YandexMetrikaMethods.reachGoal:
                this.metrika.reachGoal(...event.payload);
                break;

            default: {
                break;
            }
        }
    }

    emit(event: IYandexMetrikaEvents) {
        if (this.metrika) {
            this.handleMetrikaEvent(event);
        } else {
            this.asyncEventsBuffer.push(event);
        }
    }
}
